import checkIcon from "@tabler/icons/outline/check.svg";
import chevronRightIcon from "@tabler/icons/outline/chevron-right.svg";
import React from "react";
import { v4 as uuidv4 } from "uuid";

import { cn } from "@/utils/cn";

import { SelectDropdown } from "../features/forms";

import { Icon, HStack, Select } from "./ui";

interface ListProps {
  children: React.ReactNode;
  className?: string;
}

const List: React.FC<ListProps> = ({ children, className }) => (
  <div className={cn("space-y-0.5", className)}>{children}</div>
);

interface ListItemProps {
  label: React.ReactNode;
  hint?: React.ReactNode;
  icon?: string;
  iconClassName?: string;
  onClick?(): void;
  onSelect?(): void;
  isSelected?: boolean;
  children?: React.ReactNode;
}

const ListItem: React.FC<ListItemProps> = ({
  label,
  hint,
  icon,
  iconClassName,
  children,
  onClick,
  onSelect,
  isSelected,
}) => {
  const id = uuidv4();
  const domId = `list-group-${id}`;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onClick!();
    }
  };

  const Comp = onClick ? "a" : "div";
  const LabelComp = onClick || onSelect ? "span" : "label";
  const linkProps =
    onClick || onSelect
      ? { onClick: onClick || onSelect, onKeyDown, tabIndex: 0, role: "link" }
      : {};

  const renderChildren = React.useCallback(() => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement<{ className?: string }>(child)) {
        const isSelect = child.type === SelectDropdown || child.type === Select;

        return React.cloneElement(child, {
          // @ts-ignore
          id: domId,
          className: cn(
            {
              "w-auto": isSelect,
            },
            child.props.className,
          ),
        });
      }

      return null;
    });
  }, [children, domId]);

  return (
    <Comp
      data-testid="list-item"
      className={cn("flex items-center justify-between overflow-hidden py-1", {
        "cursor-pointer":
          typeof onClick !== "undefined" || typeof onSelect !== "undefined",
      })}
      {...linkProps}
    >
      <HStack alignItems="center" space={2.5}>
        {icon && (
          <Icon
            src={icon}
            size={24}
            className={
              iconClassName || "text-primary-600 dark:text-primary-400"
            }
          />
        )}
        <div className="flex flex-col py-1.5 pr-4 rtl:pr-0 rtl:pl-4">
          <LabelComp
            className="text-gray-900 dark:text-gray-100"
            htmlFor={domId}
          >
            {label}
          </LabelComp>

          {hint ? (
            <span className="text-sm text-muted-foreground">{hint}</span>
          ) : null}
        </div>
      </HStack>

      {onClick ? (
        <HStack
          space={1}
          alignItems="center"
          className="overflow-hidden text-muted-foreground"
        >
          {children}

          <Icon src={chevronRightIcon} className="ml-1 rtl:rotate-180" />
        </HStack>
      ) : null}

      {onSelect ? (
        <div className="flex flex-row items-center text-muted-foreground">
          {children}

          <div
            className={cn(
              "flex size-6 items-center justify-center rounded-full border-2 border-solid border-primary-500 transition dark:border-primary-400",
              {
                "bg-primary-500 dark:bg-primary-400": isSelected,
                "bg-transparent": !isSelected,
              },
            )}
          >
            <Icon
              src={checkIcon}
              className={cn(
                "size-4 text-white transition-all duration-500 dark:text-white",
                {
                  "scale-50 opacity-0": !isSelected,
                  "scale-100 opacity-100": isSelected,
                },
              )}
            />
          </div>
        </div>
      ) : null}

      {typeof onClick === "undefined" && typeof onSelect === "undefined"
        ? renderChildren()
        : null}
    </Comp>
  );
};

export { List as default, ListItem };
